'use client'

import type { ComponentProps } from 'react'
import { useId, useState } from 'react'

import { styled } from 'styled-components'

import {
  Banner,
  Button,
  FormattedMessage,
  Modal,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import { AddPaymentMethodModal } from '@b-stock/fe-core/pay-ui'

import useAccount from '@/buyer-portal/helpers/useAccount'
import { selectPaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

type Props = ComponentProps<typeof AddPaymentMethodModal>

const Content = styled(Modal.Content)`
  width: 46rem;
  max-width: 100%;

  @media ${Breakpoints.max.medium} {
    width: 37.5rem;
  }

  @media ${Breakpoints.max.small} {
    width: 100%;
  }
`

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Choice = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  color: ${Colors.Semantic.Neutral.dark_grey};

  &:focus-within {
    outline: 2px solid ${Colors.Semantic.BStock.onhover};
    outline-offset: 2px;
  }
`

const RadioInput = styled.input`
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  margin: 0;
  accent-color: ${Colors.Semantic.BStock.onhover};
`

const Title = styled.div`
  ${Typography.Subtitle2}
`

const Description = styled.div`
  ${Typography.Body3}
  margin-top: 0.25rem;
`

/**
 * Account guard for all portal add-method entry points, including the shared
 * dialog reached from settings. Saved-method management stays with each caller.
 * fe-core 180.3.0 hides ACH only in its new selector and ignores achEnabled in
 * the legacy path. Keep its bank/Stripe flow unmounted until eligibility is known.
 */
export const AddBuyerPaymentMethodModal = (props: Props) => {
  const account = useAccount(props.accountId)
  const { isACHBlocked, isCreditCardBlocked } = selectPaymentRestrictions(
    account.data?.onlinePaymentBlocked
  )
  const accountPending =
    account.isLoading || (account.isFetching && !account.data)
  // A failed background refetch retains the last known account restrictions.
  const accountReady = !!account.data && !accountPending
  const [cardSelected, setCardSelected] = useState(false)
  const descriptionId = useId()
  const { trackButtonClicked } = useAnalytics()
  const canAddCard =
    accountReady &&
    !isCreditCardBlocked &&
    props.enableSelectionFlow === true &&
    !!props.onCreditCardSelected

  if (accountReady && !isACHBlocked) {
    return <AddPaymentMethodModal {...props} />
  }

  const cancel = () => {
    props.onCancel?.()
    props.setModalOpen(false)
  }

  const continueWithCard = () => {
    if (!cardSelected || !canAddCard) return
    trackButtonClicked({
      buttonName: 'add_new_payment_initiation',
      payment_mode: 'CREDIT_CARD',
    })
    props.setModalOpen(false)
    props.onCreditCardSelected?.()
  }

  return (
    <Modal appearance="sidebar" closeModal={() => props.setModalOpen(false)}>
      <Modal.Header>
        <FormattedMessage id="AddPaymentMethodModal.title.add" />
      </Modal.Header>
      <Content>
        {accountPending ? (
          <Spinner />
        ) : !accountReady ? (
          <Banner type="warning">
            <FormattedMessage id="BlockedAchAddMethod.accountLoadError" />
            <Button
              type="button"
              appearance="tertiary"
              onClick={() => void account.refetch()}
            >
              <FormattedMessage id="BlockedAchAddMethod.retry" />
            </Button>
          </Banner>
        ) : (
          <Choices>
            <Choice $disabled>
              <RadioInput
                type="radio"
                name="paymentMethodType"
                value="ach"
                checked={false}
                disabled
                aria-describedby={descriptionId}
              />
              <div>
                <Title>
                  <FormattedMessage id="AddPaymentMethodModal.paymentMethodTypes.selection.ach.title" />
                </Title>
                <Description>
                  <FormattedMessage id="BlockedAchPicker.unavailable" />
                </Description>
                <Description id={descriptionId}>
                  <FormattedMessage id="BlockedAchPicker.message" />
                </Description>
              </div>
            </Choice>
            {canAddCard && (
              <Choice>
                <RadioInput
                  type="radio"
                  name="paymentMethodType"
                  value="cc"
                  checked={cardSelected}
                  onChange={() => setCardSelected(true)}
                />
                <div>
                  <Title>
                    <FormattedMessage id="AddPaymentMethodModal.paymentMethodTypes.selection.creditCard.title" />
                  </Title>
                  <Description>
                    <FormattedMessage id="AddPaymentMethodModal.paymentMethodTypes.selection.creditCard.description" />
                  </Description>
                </div>
              </Choice>
            )}
          </Choices>
        )}
      </Content>
      <Modal.Actions>
        <Button type="button" appearance="tertiary" onClick={cancel}>
          <FormattedMessage id="BStockNext.Common.cancel" />
        </Button>
        <Button
          type="button"
          appearance="primary"
          disabled={!cardSelected || !canAddCard}
          onClick={continueWithCard}
        >
          <FormattedMessage id="BStockReact.Common.next" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
